import { useState, useEffect, useMemo } from "react";
import { Box, Flex, Text, Button } from "@radix-ui/themes";
import { MenuItem, DatePickerWithRange, LocationMultiSelect } from "components";
import { MultiSelect } from "components/multiSelect/MultiSelect";
import { Outlet, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { 
  useProfileContext, 
  useLocationContext, 
  useDateRangeContext,
  ReviewsFilterProvider,
  useReviewsFilterContext,
  SENTIMENT_OPTIONS,
  RATING_OPTIONS,
  REPLIED_OPTIONS,
  SOURCE_OPTIONS
} from "context";
import { useGetGoogleReviewsStatus, useGetEarliestReviewDate } from "hooks";
import { useGetTripadvisorReviewsStatus } from "hooks/integrations/useGetTripadvisorReviewsStatus";
import { BuildingStorefrontIcon, DocumentMagnifyingGlassIcon, PhotoIcon, SparklesIcon, MapPinIcon, ChatBubbleOvalLeftEllipsisIcon, Squares2X2Icon, PresentationChartLineIcon } from "@heroicons/react/24/outline";

function ReviewsLayoutContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useProfileContext();
  const { initialItems, locationSelection, setLocationSelection } = useLocationContext();
  const { selectedDateRange, setSelectedDateRange, setActivePage, initializeReviewsDateRange } = useDateRangeContext();
  const {
    setSentimentFilter,
    setRatingFilter,
    setRepliedFilter,
    setSourceFilter,
  } = useReviewsFilterContext();

  // Set active page to reviews for page-specific date range
  useEffect(() => {
    setActivePage('reviews');
  }, [setActivePage]);

  // Check for Google and TripAdvisor Reviews integration
  const { data: googleReviewsStatusData } = useGetGoogleReviewsStatus(
    userProfile.organisation_id
  );
  const { data: tripadvisorReviewsStatusData } = useGetTripadvisorReviewsStatus(
    userProfile.organisation_id
  );

  const hasGoogleReviewsIntegration = googleReviewsStatusData?.data?.status === "active";
  const hasTripadvisorReviewsIntegration = tripadvisorReviewsStatusData?.data?.status === "active";
  const hasAnyReviewsIntegration = hasGoogleReviewsIntegration || hasTripadvisorReviewsIntegration;

  // Fetch earliest review date for the date picker
  const { data: earliestReviewDateData } = useGetEarliestReviewDate(
    userProfile.organisation_id,
    hasAnyReviewsIntegration // Fetch when any reviews integration is active
  );
  
  // Compute the disableBeforeDate for the date picker
  const reviewsDisableBeforeDate = useMemo(() => {
    if (earliestReviewDateData?.data?.earliest_date) {
      return new Date(earliestReviewDateData.data.earliest_date);
    }
    // Default fallback if no reviews exist
    return new Date("2024-09-01");
  }, [earliestReviewDateData]);

  // Initialize reviews date range from earliest review to today (only once)
  useEffect(() => {
    if (
      hasAnyReviewsIntegration && 
      earliestReviewDateData?.data?.earliest_date
    ) {
      const earliestDate = new Date(earliestReviewDateData.data.earliest_date);
      initializeReviewsDateRange(earliestDate);
    }
  }, [hasAnyReviewsIntegration, earliestReviewDateData, initializeReviewsDateRange]);

  // Helper function to get menu state from path
  const getMenuStateFromPath = (path) => {
    if (path.includes("/reviews/overview")) return "overview";
    if (path.includes("/reviews/competitors")) return "competitors";
    if (path.includes("/reviews/dashboard")) return "dashboard";
    if (path.includes("/reviews/reviews")) return "reviews";
    if (path.includes("/reviews/locations")) return "locations";
    if (path.includes("/reviews/business-profile")) return "business-profile";
    if (path.includes("/reviews/posts")) return "posts";
    if (path.includes("/reviews/ai-settings")) return "ai-settings";
    return "dashboard"; // default path
  };

  // State to manage menu selection
  const [reviewsMenuSelection, setReviewsMenuSelection] = useState(getMenuStateFromPath(location.pathname));

  // Function to handle menu selection
  const handleReviewsMenuSelection = (flag) => {
    setReviewsMenuSelection(flag);
    navigate(flag);
  };

  // Update menu selection on path change
  useEffect(() => {
    setReviewsMenuSelection(getMenuStateFromPath(location.pathname));
  }, [location.pathname]);

  // Reviews pages manage their own scrolling inside the main content panel.
  // Lock the document while this layout is mounted to avoid a second scrollbar
  // exposing empty space below the fixed-height application shell.
  useEffect(() => {
    document.body.classList.add("reviews-page-active");

    return () => {
      document.body.classList.remove("reviews-page-active");
    };
  }, []);

  return (
    <Flex className="reviews-layout-container" direction="column" height="100%" style={{ alignItems: "flex-start", flex: "1 0 0", alignSelf: "stretch", minWidth: 0, minHeight: 0 }}>
      
      {/* Empty state when no reviews integration */}
      {!hasAnyReviewsIntegration ? (
        <Flex px="5" py="5" style={{ alignSelf: "stretch", flex: '1 0 0', height: '100%' }}>
          <Flex style={{ alignSelf: "stretch", borderRadius: "var(--radius-4)", border: "1px dashed var(--gray-a6)", 
            flex: '1 0 0' }} height="100%" direction="column" justify="center" align="center" gap="5">
            <Flex direction="column" align="center" gap="2" maxWidth="400px" style={{alignItems: 'center', width: '100%'}}>
              <Text size="5" weight="medium" align="center">No connected reviews integration</Text>
              <Text size="2" weight="regular" align="center" color="gray">
                Connect Google or Tripadvisor to manage and analyse your reviews here.
              </Text>
            </Flex>
            <Button size="2" variant="soft" color="gray"
              onClick={() => navigate("/integrations/browse-all")}>
              Browse integrations
            </Button>
          </Flex>
        </Flex>
      ) : (
      <Flex p={{ initial: "3", sm: "5" }} direction="column" gap="8" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
        <Flex direction={{ initial: "column", md: "row" }} gap={{ initial: "5", md: "8" }} style={{ alignSelf: "stretch", flex: "1 0 0", alignItems: "stretch", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
          <Flex role="navigation" aria-label="Reviews sections" width={{ initial: "100%", md: "176px" }} direction={{ initial: "row", md: "column" }} gap="1" style={{ alignItems: "flex-start", flexShrink: 0, overflowX: "auto", paddingBottom: 4 }}>
            <MenuItem
              flag="overview"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Overview"
              tooltip="Your reputation and reviews needing attention"
              icon={<Squares2X2Icon height={16} width={16} />}
            />
            <MenuItem
              flag="dashboard"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Sentiment"
              tooltip="View sentiment analysis of all your reviews"
              icon={<DocumentMagnifyingGlassIcon height={16} width={16} />}
            />
            <MenuItem
              flag="competitors"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Competitors"
              tooltip="Explore local businesses and compare performance"
              icon={<PresentationChartLineIcon height={16} width={16} />}
            />
            <MenuItem
              flag="reviews"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Reviews"
              tooltip="Manage and analyse all your reviews"
              icon={<ChatBubbleOvalLeftEllipsisIcon height={16} width={16} />}
            />
            <MenuItem
              flag="locations"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Locations"
              tooltip="View and manage connected review integrations per location"
              icon={<MapPinIcon height={16} width={16} />}
            />
            <MenuItem
              flag="business-profile"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Business Profile"
              tooltip="Manage your Google Business Profile information"
              icon={<BuildingStorefrontIcon height={16} width={16} />}
            />
            <MenuItem
              flag="posts"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="Posts"
              tooltip="Plan photos and posts for your Google Business Profile"
              icon={<PhotoIcon height={16} width={16} />}
            />
            <MenuItem
              flag="ai-settings"
              state={reviewsMenuSelection}
              onClick={handleReviewsMenuSelection}
              text="AI Settings"
              tooltip="Configure AI settings for auto replying reviews"
              icon={<SparklesIcon height={16} width={16} />}
            />
          </Flex>
          <Flex direction="column" gap="5" role="region" aria-label="Review content" style={{ flex: "1 1 0", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
            {/* Keep page-specific filters inside the content column so navigation never shifts. */}
            <Box display={(reviewsMenuSelection === "overview" || reviewsMenuSelection === "competitors" || reviewsMenuSelection === "locations" || reviewsMenuSelection === "business-profile" || reviewsMenuSelection === "posts" || reviewsMenuSelection === "ai-settings" || !hasAnyReviewsIntegration) ? "none" : "block"} width="100%" flexShrink="0">
            {/* Filter Section */}
            <Flex justify="end" align="center" gap="2" style={{ alignSelf: "stretch", flexWrap: "wrap" }}>
              {(hasGoogleReviewsIntegration && hasTripadvisorReviewsIntegration) && (
                <MultiSelect
                  options={SOURCE_OPTIONS}
                  defaultValue={SOURCE_OPTIONS.map((option) => option.value)}
                  onValueChange={setSourceFilter}
                  placeholder="Source"
                />
              )}
              <MultiSelect
                options={SENTIMENT_OPTIONS}
                defaultValue={SENTIMENT_OPTIONS.map((option) => option.value)}
                onValueChange={setSentimentFilter}
                placeholder="Sentiment"
              />
              <MultiSelect
                options={RATING_OPTIONS}
                defaultValue={RATING_OPTIONS.map((option) => option.value)}
                onValueChange={setRatingFilter}
                placeholder="Rating"
              />
              <MultiSelect
                options={REPLIED_OPTIONS}
                defaultValue={REPLIED_OPTIONS.map((option) => option.value)}
                onValueChange={setRepliedFilter}
                placeholder="Reply status"
              />
              <DatePickerWithRange
                value={selectedDateRange}
                onDateChange={setSelectedDateRange}
                disableBeforeDate={reviewsDisableBeforeDate}
                showEndDateOnOpen
              />
              <LocationMultiSelect
                options={initialItems}
                defaultValue={locationSelection}
                onValueChange={setLocationSelection}
              />
            </Flex>
            </Box>
            <Flex style={{ flex: "1 1 0", overflow: "hidden", minWidth: 0, minHeight: 0 }}>
              <Outlet />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      )}
    </Flex>
  );
}

// Wrap with provider
export function ReviewsLayout() {
  return (
    <ReviewsFilterProvider>
      <ReviewsLayoutContent />
    </ReviewsFilterProvider>
  );
}
